import { useState } from 'react';
import { Search } from 'lucide-react';
import { PageDescription } from '@reflex/ui/components/layout/Typography';
import { Input } from '@reflex/ui/components/ui/input';
import { Badge } from '@reflex/ui/components/ui/badge';
import { JsonlViewer } from './JsonlViewer';
import { StructuredViewer } from './StructuredViewer';
import { EnvViewer } from './EnvViewer';
import { PlainViewer } from './PlainViewer';
import { detectFormat, FORMAT_LABELS } from './log-viewer-utils';

interface LogViewerProps {
  content: string;
  path: string;
}

export function LogViewer({ content, path }: LogViewerProps) {
  const [filter, setFilter] = useState('');

  if (!content || content === '(empty)' || content === '(file not found)') {
    return (
      <div className="flex items-center justify-center py-8">
        <PageDescription>{content || '(empty)'}</PageDescription>
      </div>
    );
  }

  const format = detectFormat(path, content);
  const lines = content.split('\n');

  const lineCount = lines.filter((l) => l.trim()).length;

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter logs…"
            className="h-7 pl-8 text-xs font-mono bg-muted/30"
          />
        </div>
        <Badge variant="secondary" className="text-3xs px-1.5 py-0 shrink-0 font-mono font-medium">
          {lineCount} {format === 'env' ? 'vars' : 'lines'}
        </Badge>
        <Badge variant="outline" className="text-3xs px-1.5 py-0 shrink-0 font-medium">
          {FORMAT_LABELS[format]}
        </Badge>
      </div>
      <div
        className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-muted/20 p-2"
        tabIndex={0}
        role="region"
        aria-label="Log output"
      >
        {format === 'jsonl' && <JsonlViewer lines={lines} filter={filter} />}
        {format === 'structured' && <StructuredViewer lines={lines} filter={filter} />}
        {format === 'env' && <EnvViewer content={content} filter={filter} />}
        {format === 'plain' && <PlainViewer lines={lines} filter={filter} />}
      </div>
    </div>
  );
}
